/**
 * Andamenti di un movimento nel tempo: la stessa quantita' letta per settimana,
 * seduta, mese o programma, sempre con la finestra dichiarata.
 *
 * - Il movimento si sceglie fra «tutto il lavoro», le **famiglie di alzate**
 *   (snatch e clean & jerk con tutte le varianti a bilanciere, D27) e i
 *   singoli movimenti; di una famiglia si vedono le varianti entrate nel
 *   conteggio e se ne toglie una con un clic.
 * - Sopra i grafici due **bande**, programmi e mesocicli, allineate ai secchi
 *   come la striscia stagionale del builder (D29).
 * - Un **grafico piccolo per misura**, uno sotto l'altro sullo stesso asse del
 *   tempo (serie, ripetizioni, tonnellaggio, carico, intensita', RPE…): due
 *   misure con scale diverse non stanno mai sullo stesso asse. Il tooltip e'
 *   sincronizzato, cosi' un periodo si legge su tutti i pannelli insieme.
 * - L'**intensita'** e' la % prescritta dal programma e la % realizzata sul
 *   massimale di riferimento della piattaforma (D28), dichiarato in chiaro.
 * - La **tabella gemella** mostra solo le colonne con un valore, con
 *   intestazioni brevi: niente scorrimento orizzontale.
 * - Si aprono subito **tre misure** scelte per il tipo di movimento
 *   (`splitTrendPanels`), le altre stanno a un clic; le regole di lettura sono
 *   tutte sotto «Come si leggono», chiuse (D32).
 *
 * Periodo, raggruppamento e movimento arrivano da `AthleteTrends`.
 *
 * Legge `GET /coach/monitoring/athletes/{id}/trend`. Spec: docs/monitoring/ANALYSIS.md §5.6 e §5.8.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarRange, ChevronDown, Layers, Loader2 } from "lucide-react";
import {
	Bar,
	CartesianGrid,
	ComposedChart,
	Line,
	ResponsiveContainer,
	Tooltip as RechartsTooltip,
	XAxis,
	YAxis,
} from "recharts";
import { fetchAPI } from "../../lib/utils";
import { useChartColors, useChartTooltipStyle, useReadableColor } from "../../lib/useChartColors";
import {
	availableTrendPanels,
	familyLabel,
	formatMetricValue,
	formatPercentRange,
	formatPlainNumber,
	formatShortDay,
	panelChartData,
	phaseBands,
	programBands,
	splitTrendPanels,
	trendBucketLabel,
	visibleTrendColumns,
} from "../../lib/monitoringUtils";
import { Button } from "../ui/button";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { MovementQuantitiesTable } from "./MovementQuantitiesTable";
import { useIsCompact } from "./primitives";

const BUCKET_LABEL_KEYS = {
	week: "monBucketWeek",
	session: "monBucketSession",
	month: "monBucketMonth",
	program: "monBucketProgram",
};

// Il bordo sinistro e destro dell'area dei dati in ogni grafico: asse Y largo
// 56 con margine -8, margine destro 8. Le bande sopra i grafici usano gli
// stessi numeri, cosi' un tratto copre esattamente i suoi secchi senza
// misurare niente (stessa idea della griglia a colonne del `SeasonStrip`).
const Y_AXIS_WIDTH = 56;
const PLOT_MARGIN = { top: 6, right: 8, bottom: 0, left: -8 };
const PLOT_LEFT = Y_AXIS_WIDTH + PLOT_MARGIN.left;
const PLOT_RIGHT = PLOT_MARGIN.right;

const REFERENCE_SOURCE_KEYS = { manual: "monRefManual", "1rm_test": "monRefTest", auto_1rm_estimate: "monRefEstimate" };

function seriesColor(series, colors) {
	if (series.role === "muted") return colors.axisFaint;
	if (series.role === "second") return colors.blue;
	return colors.volt;
}

function tickFormatter(unit, language) {
	return (value) => {
		if (unit === "kg" && Math.abs(value) >= 1000) return `${Math.round(value / 100) / 10}k`;
		if (unit === "percent") return `${value}%`;
		if (unit === "count" || unit === "rpe") return String(value);
		return formatMetricValue(value, unit, language);
	};
}

/** Legenda testuale di un pannello: solo con due o piu' serie (una sola la nomina il titolo). */
function PanelLegend({ panel, colors, t }) {
	const items = panel.series.filter((series) => series.legend !== false);
	if (items.length < 2) return null;
	return (
		<ul className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
			{items.map((series) => (
				<li key={series.field} className="flex items-center gap-1.5">
					<span
						aria-hidden="true"
						className={series.kind === "line" ? "inline-block w-3 h-0.5 rounded-full" : "inline-block w-2.5 h-2.5 rounded-sm"}
						style={{ backgroundColor: seriesColor(series, colors), opacity: series.role === "muted" ? 0.7 : 1 }}
					/>
					{t(series.labelKey)}
				</li>
			))}
		</ul>
	);
}

function SmallMultiple({ panel, data, isLast, t, language }) {
	const colors = useChartColors();
	const tooltipStyle = useChartTooltipStyle();
	const format = (value) => formatMetricValue(value, panel.unit, language);
	return (
		<section className="space-y-1" data-testid={`trend-panel-${panel.key}`}>
			<div className="flex items-center justify-between gap-2 pl-1">
				<h4 className="text-[11px] uppercase tracking-widest text-muted-foreground">{t(panel.labelKey)}</h4>
				<PanelLegend panel={panel} colors={colors} t={t} />
			</div>
			<div style={{ height: isLast ? 150 : 118 }}>
				<ResponsiveContainer width="100%" height="100%">
					<ComposedChart data={data} syncId="monitoring-trend" margin={PLOT_MARGIN} barCategoryGap="30%" barGap={2}>
						<CartesianGrid stroke={colors.grid} vertical={false} />
						<XAxis
							dataKey="label"
							hide={!isLast}
							stroke={colors.axis}
							tick={{ fill: colors.axis, fontSize: 10 }}
							tickLine={false}
							interval="preserveStartEnd"
						/>
						<YAxis
							width={Y_AXIS_WIDTH}
							stroke={colors.axis}
							tick={{ fill: colors.axis, fontSize: 10 }}
							tickLine={false}
							axisLine={false}
							domain={panel.domain || [0, "auto"]}
							allowDecimals={panel.unit !== "count"}
							tickFormatter={tickFormatter(panel.unit, language)}
						/>
						<RechartsTooltip
							contentStyle={tooltipStyle}
							labelStyle={{ color: colors.axis }}
							cursor={{ fill: colors.gridSoft, opacity: 0.4 }}
							labelFormatter={(label, payload) => {
								const point = payload?.[0]?.payload;
								return point?.from && point?.to && point.from !== point.to
									? `${label} · ${formatShortDay(point.from, language)} – ${formatShortDay(point.to, language)}`
									: label;
							}}
							formatter={(value, name) => [format(value), name]}
						/>
						{panel.series.map((series) =>
							series.kind === "line" ? (
								<Line
									key={series.field}
									type="monotone"
									dataKey={series.field}
									name={t(series.labelKey)}
									stroke={seriesColor(series, colors)}
									strokeWidth={2}
									dot={{ r: 3, fill: seriesColor(series, colors), strokeWidth: 0 }}
									activeDot={{ r: 4 }}
									connectNulls={false}
									isAnimationActive={false}
								/>
							) : (
								<Bar
									key={series.field}
									dataKey={series.field}
									name={t(series.labelKey)}
									fill={seriesColor(series, colors)}
									fillOpacity={series.role === "muted" ? 0.55 : series.kind === "range" ? 0.45 : 1}
									maxBarSize={24}
									radius={series.kind === "range" ? [4, 4, 4, 4] : [4, 4, 0, 0]}
									isAnimationActive={false}
								/>
							),
						)}
					</ComposedChart>
				</ResponsiveContainer>
			</div>
		</section>
	);
}

/** Una riga di banda: icona nel margine dell'asse Y, poi una griglia con una colonna per secchio. */
function BandRow({ icon: Icon, label, segments, count, colorOf, titleOf }) {
	return (
		<div className="flex items-stretch" style={{ paddingRight: PLOT_RIGHT }}>
			<div
				className="flex items-center justify-end pr-1.5 text-muted-foreground shrink-0"
				style={{ width: PLOT_LEFT }}
				title={label}
				aria-label={label}
			>
				<Icon className="w-3 h-3" aria-hidden="true" />
			</div>
			<div className="grid flex-1 gap-x-0.5 min-w-0" style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}>
				{segments.map((segment) => {
					const color = colorOf(segment);
					return (
						<div
							key={`${segment.id}-${segment.startIndex}`}
							title={titleOf(segment)}
							style={{
								gridColumn: `${segment.startIndex + 1} / span ${segment.span}`,
								...(color ? { backgroundColor: `${color}26`, borderColor: `${color}80`, color } : {}),
							}}
							className={`h-5 min-w-0 rounded-sm border px-1.5 text-[10px] font-heading uppercase tracking-wider leading-[18px] truncate ${
								color ? "" : "border-border bg-muted/40 text-muted-foreground"
							}`}
						>
							{segment.item.label}
						</div>
					);
				})}
			</div>
		</div>
	);
}

function TrendBands({ programs, phases, count, granularity, t, language }) {
	const readable = useReadableColor();
	if (!count || granularity === "program") return null;
	const phaseTitle = (segment) => {
		const phase = segment.item.phase || {};
		const dates = `${formatShortDay(phase.start_date, language)} – ${formatShortDay(phase.end_date || phase.start_date, language)}`;
		return [phase.name, phase.group_name, phase.description, dates].filter(Boolean).join("\n");
	};
	return (
		<div className="space-y-1" data-testid="trend-bands">
			{programs.length ? (
				<BandRow
					icon={Layers}
					label={t("monBandsPrograms")}
					segments={programs}
					count={count}
					colorOf={() => null}
					titleOf={(segment) => segment.item.label}
				/>
			) : null}
			{phases.length ? (
				<BandRow
					icon={CalendarRange}
					label={t("monBandsPhases")}
					segments={phases}
					count={count}
					colorOf={(segment) => readable(segment.item.color || "#007AFF")}
					titleOf={phaseTitle}
				/>
			) : (
				<p className="text-[10px] uppercase tracking-wider text-muted-foreground" style={{ paddingLeft: PLOT_LEFT }}>
					{t("monBandsNoPhases")}
				</p>
			)}
		</div>
	);
}

function AdherenceChart({ buckets, granularity, t, language }) {
	const colors = useChartColors();
	const tooltipStyle = useChartTooltipStyle();
	const data = useMemo(
		() =>
			buckets.map((bucket) => ({
				label: trendBucketLabel(bucket, granularity, language),
				assigned: bucket.assigned_days,
				done: bucket.days_with_outcome,
			})),
		[buckets, granularity, language],
	);
	if (!data.some((point) => point.assigned)) return null;
	return (
		<div className="h-[150px]">
			<ResponsiveContainer width="100%" height="100%">
				<ComposedChart data={data} margin={PLOT_MARGIN} barCategoryGap="30%" barGap={2}>
					<CartesianGrid stroke={colors.grid} vertical={false} />
					<XAxis dataKey="label" stroke={colors.axis} tick={{ fill: colors.axis, fontSize: 10 }} tickLine={false} interval="preserveStartEnd" />
					<YAxis width={Y_AXIS_WIDTH} stroke={colors.axis} tick={{ fill: colors.axis, fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
					<RechartsTooltip
						contentStyle={tooltipStyle}
						labelStyle={{ color: colors.axis }}
						cursor={{ fill: colors.gridSoft, opacity: 0.4 }}
						formatter={(value, name) => [value, name]}
					/>
					<Bar dataKey="assigned" name={t("monAssignedDays")} fill={colors.axisFaint} fillOpacity={0.55} maxBarSize={24} radius={[4, 4, 0, 0]} isAnimationActive={false} />
					<Bar dataKey="done" name={t("monDaysWithOutcome")} fill={colors.volt} maxBarSize={24} radius={[4, 4, 0, 0]} isAnimationActive={false} />
				</ComposedChart>
			</ResponsiveContainer>
		</div>
	);
}

/** La cella della tabella gemella per una colonna: il numero e, dove c'e', il programmato accanto. */
function cellFor(column, row, language) {
	const planned = (value) => <span className="text-muted-foreground"> / {formatPlainNumber(value, language)}</span>;
	switch (column.key) {
		case "sessions":
			return row.sessions || "—";
		case "sets":
			return (
				<>
					{row.sets || "—"}
					{row.planned_sets ? planned(row.planned_sets) : null}
				</>
			);
		case "reps":
			return (
				<>
					{row.reps ? formatPlainNumber(row.reps, language) : "—"}
					{row.planned_reps ? planned(row.planned_reps) : null}
				</>
			);
		case "tonnage_kg":
		case "top_load_kg":
		case "avg_load_kg":
			return formatPlainNumber(row[column.key], language);
		case "planned_pct":
			return formatPercentRange(row.planned_pct_avg, row.planned_pct_min, row.planned_pct_max);
		case "intensity":
			return formatPercentRange(row.intensity_avg, row.intensity_min, row.intensity_max);
		case "rpe":
			return (
				<>
					{row.rpe_avg ?? "—"}
					{row.rpe_max ? <span className="text-muted-foreground"> · {row.rpe_max}</span> : null}
				</>
			);
		case "meters":
			return formatPlainNumber(row.meters || null, language);
		case "seconds":
			return formatMetricValue(row.seconds || null, "seconds", language);
		case "calories":
			return formatPlainNumber(row.calories || null, language);
		case "adherence":
			return row.assigned_days ? `${row.days_with_outcome}/${row.assigned_days}` : "—";
		default:
			return "—";
	}
}

/** Le note dei grafici: sul telefono stanno sotto «Come si leggono», per non separare i grafici dalla tabella. */
function ReadingNotes({ t, children }) {
	return (
		<details className="group" data-testid="trend-reading-notes">
			<summary className="cursor-pointer select-none text-[11px] uppercase tracking-wider text-muted-foreground">
				{t("monHowToRead")}
			</summary>
			<div className="mt-2 space-y-2">{children}</div>
		</details>
	);
}

/** La tabella gemella sul telefono: una scheda per periodo, le misure in una griglia da tre. */
function BucketCards({ buckets, columns, bucket, t, language }) {
	return (
		<ul className="space-y-2" data-testid="trend-bucket-cards">
			{buckets.map((row) => (
				<li key={row.key} className="rounded-sm border border-border bg-card px-3 py-2 tabular-nums">
					<div className="flex items-baseline justify-between gap-2">
						<span className="text-sm font-medium">{trendBucketLabel(row, bucket, language)}</span>
						{row.from && row.to && row.from !== row.to ? (
							<span className="text-[10px] text-muted-foreground">
								{formatShortDay(row.from, language)} – {formatShortDay(row.to, language)}
							</span>
						) : null}
					</div>
					<dl className="mt-1.5 grid grid-cols-3 gap-x-3 gap-y-1.5">
						{columns.map((column) => (
							<div key={column.key} className="min-w-0">
								<dt className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">{t(column.shortKey)}</dt>
								<dd className="text-xs">{cellFor(column, row, language)}</dd>
							</div>
						))}
					</dl>
				</li>
			))}
		</ul>
	);
}

export function ExerciseTrendPanel({
	athleteId,
	weeks = 12,
	bucket = "week",
	movement = "all",
	onMovementChange,
	today,
	programId,
	t,
	language,
}) {
	const compact = useIsCompact();
	// Le varianti di una famiglia tolte dal conteggio (chiavi di movimento).
	const [excluded, setExcluded] = useState([]);
	const [showMore, setShowMore] = useState(false);
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);

	// Un movimento nuovo riparte con tutte le varianti dentro.
	useEffect(() => {
		setExcluded([]);
	}, [movement]);

	const selectMovement = (key) => onMovementChange?.(key);
	const toggleMember = (key) =>
		setExcluded((current) => (current.includes(key) ? current.filter((item) => item !== key) : [...current, key]));

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const params = new URLSearchParams({ weeks: String(weeks), bucket, today });
			if (movement !== "all") params.set("movement", movement);
			excluded.forEach((key) => params.append("exclude", key));
			if (programId && programId !== "all") params.set("program_id", programId);
			setData(await fetchAPI(`/coach/monitoring/athletes/${athleteId}/trend?${params.toString()}`));
		} catch (error) {
			toast.error(error?.message || t("monLoadError"));
		} finally {
			setLoading(false);
		}
	}, [athleteId, weeks, bucket, movement, excluded, programId, today, t]);

	useEffect(() => {
		load();
	}, [load]);

	const buckets = useMemo(() => data?.buckets || [], [data]);
	const panels = useMemo(() => availableTrendPanels(buckets), [buckets]);
	const { primary: primaryPanels, more: morePanels } = useMemo(() => splitTrendPanels(panels), [panels]);
	const shownPanels = showMore ? [...primaryPanels, ...morePanels] : primaryPanels;
	const columns = useMemo(() => visibleTrendColumns(buckets), [buckets]);
	const programSegments = useMemo(() => programBands(buckets, data?.programs, bucket), [buckets, data, bucket]);
	const phaseSegments = useMemo(() => phaseBands(buckets, data?.phases, bucket), [buckets, data, bucket]);
	const selection = data?.selection || null;
	const reference = data?.reference || null;
	const movementOptions = useMemo(() => {
		const rows = data?.movements || [];
		// La chiave scelta da fuori potrebbe non avere esposizioni nel periodo: resta selezionabile.
		if (movement !== "all" && !movement.startsWith("family:") && !rows.some((row) => row.key === movement)) {
			return [...rows, { key: movement, exercise_name: movement.replace(/^name:/, ""), exposures: 0 }];
		}
		return rows;
	}, [data, movement]);
	const familyOptions = useMemo(() => {
		const rows = data?.families || [];
		if (movement.startsWith("family:") && !rows.some((row) => row.key === movement)) {
			return [...rows, { key: movement, family_id: movement.slice("family:".length), label: movement, exposures: 0, members: [] }];
		}
		return rows;
	}, [data, movement]);
	const rangeLabel = data ? `${formatShortDay(data.range.from, language)} – ${formatShortDay(data.range.to, language)}` : "";
	const head = "px-2 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-medium whitespace-nowrap";
	const note = "text-[11px] text-muted-foreground";

	return (
		<div className="space-y-4" data-testid="exercise-trend-panel">
			<div className="space-y-1">
				<Select value={movement} onValueChange={selectMovement}>
					<SelectTrigger className="rounded-sm w-full sm:max-w-md" aria-label={t("monColMovement")}>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">{t("monAllMovements")}</SelectItem>
						{familyOptions.length ? (
							<SelectGroup>
								<SelectLabel>{t("monFamiliesGroup")}</SelectLabel>
								{familyOptions.map((family) => (
									<SelectItem key={family.key} value={family.key}>
										{familyLabel(family, t)}
										{family.exposures ? ` · ${family.exposures} ${t("monTimesShort")}` : ""}
									</SelectItem>
								))}
							</SelectGroup>
						) : null}
						<SelectGroup>
							<SelectLabel>{t("monMovementsGroup")}</SelectLabel>
							{movementOptions.map((item) => (
								<SelectItem key={item.key} value={item.key}>
									{item.exercise_name}
									{item.exposures ? ` · ${item.exposures} ${t("monTimesShort")}` : ""}
								</SelectItem>
							))}
						</SelectGroup>
					</SelectContent>
				</Select>
				{rangeLabel ? <p className="text-xs text-muted-foreground">{rangeLabel}</p> : null}
			</div>

			{selection?.kind === "family" ? (
				<div className="rounded-sm border border-border bg-card p-3 space-y-2" data-testid="family-members">
					<div className="text-[10px] uppercase tracking-widest text-muted-foreground">
						{t("monFamilyMembers")} · {familyLabel(selection, t)}
					</div>
					{selection.members?.length ? (
						<div className="flex flex-wrap gap-1.5">
							{selection.members.map((member) => (
								<button
									key={member.key}
									type="button"
									aria-pressed={!member.excluded}
									onClick={() => toggleMember(member.key)}
									data-testid={`family-member-${member.key}`}
									className={`rounded-sm border px-2 py-0.5 text-xs transition-colors ${
										member.excluded
											? "border-border text-muted-foreground line-through opacity-70"
											: "border-volt/50 bg-volt/10 text-foreground"
									}`}
								>
									{member.exercise_name} · {member.exposures}
								</button>
							))}
						</div>
					) : (
						<p className="text-xs text-muted-foreground">{t("monFamilyNoMembers")}</p>
					)}
					<p className={note}>{t("monFamilyMembersNote")}</p>
				</div>
			) : null}
			{selection?.kind === "movement" && selection.family_hint ? (
				<p className="text-xs text-muted-foreground flex flex-wrap items-center gap-2" data-testid="family-hint">
					<span>
						{t("monFamilyHint")} «{familyLabel(selection.family_hint, t)}».
					</span>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="rounded-sm h-7 px-2 text-xs"
						onClick={() => selectMovement(selection.family_hint.key)}
					>
						{t("monFamilyOpen")}
					</Button>
				</p>
			) : null}

			<div className={loading && data ? "opacity-60 transition-opacity space-y-4" : "transition-opacity space-y-4"}>
				{loading && !data ? (
					<div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
						<Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
						{t("loading")}
					</div>
				) : null}
				{data && !buckets.length ? (
					<div className="rounded-sm border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
						{t("monNoMovements")}
					</div>
				) : null}
				{buckets.length ? (
					<>
						<section className="rounded-sm border border-border bg-card p-4 space-y-3">
							<h3 className="text-xs uppercase tracking-widest text-muted-foreground">
								{t("monChartsTitle")} · {t(BUCKET_LABEL_KEYS[bucket])}
							</h3>
							<TrendBands
								programs={programSegments}
								phases={phaseSegments}
								count={buckets.length}
								granularity={bucket}
								t={t}
								language={language}
							/>
							{shownPanels.map((panel, index) => (
								<SmallMultiple
									key={panel.key}
									panel={panel}
									data={panelChartData(buckets, panel, bucket, language)}
									isLast={index === shownPanels.length - 1}
									t={t}
									language={language}
								/>
							))}
							{morePanels.length ? (
								<Button
									type="button"
									variant="ghost"
									size="sm"
									className="rounded-sm h-8 px-2 text-xs"
									aria-expanded={showMore}
									onClick={() => setShowMore((value) => !value)}
									data-testid="trend-more-measures"
								>
									<ChevronDown
										className={`w-3.5 h-3.5 mr-1 transition-transform ${showMore ? "rotate-180" : ""}`}
										aria-hidden="true"
									/>
									{showMore
										? t("monTrendsFewerMeasures")
										: `${t("monTrendsMoreMeasures")}: ${morePanels.map((panel) => t(panel.labelKey)).join(", ")}`}
								</Button>
							) : null}
							{selection && selection.kind !== "all" ? (
								<p className="text-xs text-muted-foreground" data-testid="intensity-reference">
									{reference
										? `${t("monIntensityReference")}: ${reference.name} ${formatMetricValue(reference.kg, "kg", language)} · ${t(
												REFERENCE_SOURCE_KEYS[reference.source_type] || "monRefEstimate",
											)}${reference.date ? ` ${formatShortDay(reference.date, language)}` : ""}`
										: t("monIntensityNoReference")}
								</p>
							) : null}
							<ReadingNotes t={t}>
								<p className={note}>{t("monTrendWindowNote")}</p>
								{movement === "all" ? <p className={note}>{t("monTrendAllMovementsNote")}</p> : null}
								<p className={note}>{t("monChartsNote")}</p>
										{panels.some((panel) => panel.key === "tonnage" || panel.key === "load") ? (
									<p className={note}>{t("monTrendLoadNote")}</p>
								) : null}
								{panels.some((panel) => panel.key === "intensity") ? <p className={note}>{t("monIntensityNote")}</p> : null}
							</ReadingNotes>
						</section>
						<section className="rounded-sm border border-border bg-card p-4 space-y-2">
							<h3 className="text-xs uppercase tracking-widest text-muted-foreground">{t("monTrendAdherence")}</h3>
							<AdherenceChart buckets={buckets} granularity={bucket} t={t} language={language} />
							<p className={note}>{t("monTrendAdherenceNote")}</p>
						</section>
						{compact ? (
							<BucketCards buckets={buckets} columns={columns} bucket={bucket} t={t} language={language} />
						) : (
							<div className="rounded-sm border border-border bg-card overflow-x-auto">
								<table className="w-full text-xs">
									<thead className="border-b border-border">
										<tr>
											<th className={`${head} text-left`}>{t("monColPeriod")}</th>
											{columns.map((column) => (
												<th key={column.key} className={`${head} text-right`} title={t(column.labelKey)} aria-label={t(column.labelKey)}>
													{t(column.shortKey)}
												</th>
											))}
										</tr>
									</thead>
									<tbody>
										{buckets.map((row) => (
											<tr key={row.key} className="border-b border-border/60 last:border-b-0 tabular-nums">
												<td className="px-2 py-1.5 whitespace-nowrap">
													<div>{trendBucketLabel(row, bucket, language)}</div>
													{row.from && row.to && row.from !== row.to ? (
														<div className="text-[10px] text-muted-foreground">
															{formatShortDay(row.from, language)} – {formatShortDay(row.to, language)}
														</div>
													) : null}
												</td>
												{columns.map((column) => (
													<td key={column.key} className="px-2 py-1.5 text-right whitespace-nowrap">
														{cellFor(column, row, language)}
													</td>
												))}
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
						<section className="space-y-2">
							<h3 className="text-xs uppercase tracking-widest text-muted-foreground">
								{t("monTrendTotals")} · {rangeLabel}
							</h3>
							<MovementQuantitiesTable
								movements={data.movements_table || []}
								totals={data.totals}
								onSelect={movement === "all" ? selectMovement : null}
								t={t}
								language={language}
							/>
						</section>
					</>
				) : null}
			</div>
		</div>
	);
}
